import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useDashboardQuery } from './useDashboard';
import type { DashboardAttentionItem, DashboardAttentionType } from './useDashboard';
import { Skeleton } from '@/components/ui';

/**
 * EAA-1814 (S3 rebuild — canonical frame 09): "Cần bạn xử lý" (frame 09 lines
 * 628-789), now reading the real `attention[]` array from `GET /dashboard`
 * instead of the S2 pass's two-source client merge.
 *
 * FIXED ORDER (per ticket): the same order `DashboardService._attention()`
 * appends items in (services.py lines 1694-1756) — role map paused →
 * assignment sync error → app pending approval → orphan Level. This is a
 * WIDER set than the S2 queue: that pass explicitly dropped "assignment
 * sync_status=error" because portal-api had no cross-org aggregate for it at
 * the time; EAA-1829's `_attention()` now computes it server-side (one
 * aggregate query, org-scoped), so it is restored here.
 *
 * `attention` only ever lists entries with `count > 0` (service docstring) —
 * an empty array is a real, fully-resolved "nothing pending" state, so the
 * green pill below is honest, not a guessed default.
 *
 * ROW SHAPE vs the frame literal: frame 09 (lines 663-789) draws each row as
 * an icon chip + text + a STATIC status pill ("Chặn"/"Chờ"/"Nhắc") with no
 * href at all — only the card's "Xem tất cả" button is a real control. The
 * ticket's own spec overrides that for interaction: "each row deep-linking
 * to the screen that fixes it" (WHAT THE FRAME SHOWS section) is an explicit
 * functional requirement, so each row here is the whole `target` link, with
 * the frame's icon-chip + pill styling preserved as the row's visual chrome.
 * "Xem tất cả" is NOT built: there is no aggregate "all issues" page to link
 * to (each attention type already has its own target) — see report.
 *
 * Vietnamese copy per row is built HERE from `type`, not the backend's
 * `label` (an internal English string — see useDashboard.ts) —
 * glossary-compliant: "Level sai khoá vai trò", never "Level mồ côi"; "liên
 * kết vai trò", never "ánh xạ".
 */

const ORDER: DashboardAttentionType[] = [
    'role_map_paused',
    'assignment_sync_error',
    'app_pending_approval',
    'orphan_level',
];

interface RowCopy {
    severity: 'danger' | 'warning';
    icon: string;
    pill: string;
    title: string;
    meta: string;
}

// EAA-1845: takes `t` explicitly rather than calling useTranslation() itself —
// this is a plain function (not a component/hook), called from inside the
// HomeActionQueue render, so it receives the already-resolved translator.
function copyFor(item: DashboardAttentionItem, t: TFunction): RowCopy {
    switch (item.type) {
        case 'role_map_paused':
            return {
                severity: 'danger',
                icon: '!',
                pill: t('home.actionQueue.pillBlocked'),
                title: t('home.actionQueue.roleMapPausedTitle', { count: item.count }),
                meta: t('home.actionQueue.roleMapPausedMeta'),
            };
        case 'assignment_sync_error':
            return {
                severity: 'danger',
                icon: '!',
                pill: t('home.actionQueue.pillError'),
                title: t('home.actionQueue.assignmentSyncErrorTitle', { count: item.count }),
                meta: t('home.actionQueue.assignmentSyncErrorMeta'),
            };
        case 'app_pending_approval':
            return {
                severity: 'warning',
                icon: '◔',
                pill: t('home.actionQueue.pillPending'),
                title: t('home.actionQueue.appPendingApprovalTitle', { count: item.count }),
                meta: t('home.actionQueue.appPendingApprovalMeta'),
            };
        case 'orphan_level':
            return {
                severity: 'warning',
                icon: '◔',
                pill: t('home.actionQueue.pillWarning'),
                title: t('home.actionQueue.orphanLevelTitle', { count: item.count }),
                meta: t('home.actionQueue.orphanLevelMeta'),
            };
        default:
            // Forward-compat: an unrecognised future type still renders, using
            // the backend's own label, rather than silently disappearing.
            return {
                severity: 'warning',
                icon: '◔',
                pill: t('home.actionQueue.pillNotice'),
                title: t('home.actionQueue.unknownTitle', { count: item.count, label: item.label }),
                meta: item.label,
            };
    }
}

/** Which capability gates each attention row — there is no `console.home.*`
 *  capability, so this mirrors the capability that owns the row's `target`. */
function capabilityFor(type: string): string {
    switch (type) {
        case 'role_map_paused':
        case 'orphan_level':
            return 'console.rolemap.view';
        case 'assignment_sync_error':
            return 'console.orgs.view';
        case 'app_pending_approval':
            return 'console.apps.view';
        default:
            return 'console.rolemap.view';
    }
}

export function HomeActionQueue({
    can,
    apiBase,
    accessToken,
    authenticated,
}: {
    can: (key: string) => boolean;
    apiBase: string;
    accessToken: string | null;
    authenticated: boolean;
}) {
    const { t } = useTranslation();
    const canRoleMap = can('console.rolemap.view');
    const canOrgs = can('console.orgs.view');
    const canApps = can('console.apps.view');
    const baseEnabled = authenticated && Boolean(accessToken);

    const dashboardQuery = useDashboardQuery(
        apiBase,
        accessToken,
        baseEnabled && (canRoleMap || canOrgs || canApps)
    );

    if (!canRoleMap && !canOrgs && !canApps) {
        return null;
    }

    const byType = new Map((dashboardQuery.data?.attention ?? []).map((item) => [item.type, item]));
    const items = ORDER.map((type) => byType.get(type))
        .filter((item): item is DashboardAttentionItem => Boolean(item))
        .filter((item) => can(capabilityFor(item.type)));

    const loading = dashboardQuery.isLoading;
    const hasError = dashboardQuery.isError;

    return (
        <div className="portal-home-card portal-home-queue-card">
            <div className="portal-home-card-head">
                <div className="portal-home-card-head-text">
                    <h3>{t('home.actionQueue.title')}</h3>
                    <span className="portal-home-card-sub">
                        {t('home.actionQueue.subtitle')}
                    </span>
                </div>
            </div>

            {loading && (
                <div className="portal-home-queue-skeleton" role="status" aria-live="polite">
                    <span className="portal-sr-only">{t('home.loadingSr')}</span>
                    <Skeleton height="3.25rem" />
                    <Skeleton height="3.25rem" />
                </div>
            )}

            {!loading && hasError && (
                <p className="portal-home-queue-error">
                    {t('home.actionQueue.loadError')}{' '}
                    <button
                        type="button"
                        className="portal-home-kpi-retry"
                        onClick={() => dashboardQuery.refetch()}
                    >
                        {t('home.retry')}
                    </button>
                </p>
            )}

            {!loading && !hasError && items.length === 0 && (
                <div className="portal-home-queue-empty">
                    <span className="portal-home-queue-pill">{t('home.actionQueue.emptyPill')}</span>
                    <span className="portal-home-queue-empty-count">
                        {t('home.actionQueue.emptyCount')}
                    </span>
                </div>
            )}

            {!loading && !hasError && items.length > 0 && (
                <ul className="portal-home-queue-list">
                    {items.map((item) => {
                        const { severity, icon, pill, title, meta } = copyFor(item, t);
                        return (
                            <li key={item.type}>
                                <Link to={item.target} className="portal-home-queue-row">
                                    <span
                                        className={`portal-home-queue-icon is-${severity}`}
                                        aria-hidden="true"
                                    >
                                        {icon}
                                    </span>
                                    <span className="portal-home-queue-row-text">
                                        <strong>{title}</strong>
                                        <span className="portal-home-queue-row-meta">{meta}</span>
                                    </span>
                                    <span className={`portal-home-queue-status is-${severity}`}>
                                        {pill}
                                    </span>
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

export default HomeActionQueue;
